import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Plus, X, Car } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { useDeliveryProofForm } from '@/features/delivery-proof/hooks/useDeliveryProofForm';
import { useDeliveryProofStore } from '@/features/delivery-proof/stores/deliveryProofStore';
import { useDeliveryProof } from '@/features/delivery-proof/api/deliveryProofApi';
import { FileUploadField } from '@/features/delivery-proof/components/FileUploadField';
import { FileDropzone } from '@/Components/shared/FileDropzone';
import {
  DeliveryProofVehicleInfoType,
  type DeliveryProofAttachment,
  type VehicleInfoItemDto,
} from '@/features/delivery-proof/types/delivery-proof.types';
import { toAttachment } from '@/features/delivery-proof/utils/attachments';
import { DeliveryProofConfirmationModal } from '@/features/delivery-proof/components/DeliveryProofConfirmationModal';
import type { WizardStepHandle } from '@/Components/shared/WizardLayout.types';

const PRESENTATION_LABELS = ['Face', 'Profil conducteur', 'Profil passager', 'Arrière', 'Soubassement', 'Bloc moteur'];

// Longueur max d'une observation : la colonne `value` de
// pro_v2_delivery_proof_vehicle_info est un varchar(255). Au-delà, l'API
// remonte un 500 (Data too long) — d'où le plafond appliqué à la saisie.
// Pour un besoin plus long, ajouter une seconde observation.
const OBSERVATION_MAX_LENGTH = 255;

// Découpe au POINT DE CODE, pas à l'unité UTF-16 : `slice()` couperait une
// paire de substitution en plein milieu (un emoji en 255e position laisse un
// substitut isolé), que MySQL rejette en utf8mb4 — soit exactement le 500 que
// ce plafond doit éviter. C'est aussi la bonne sémantique : un varchar(255)
// compte des caractères, pas des unités UTF-16.
const capObservation = (text: string) =>
  [...text].slice(0, OBSERVATION_MAX_LENGTH).join('');
const observationLength = (text: string) => [...text].length;

const ICON_CLASS = 'tw-w-16 tw-h-16 tw-object-contain';

// Compteur et présentation : des photos affichées dans le corps de
// l'attestation, où seule la page 1 d'un PDF serait visible.
const PHOTO_ACCEPT = 'image/png, image/jpeg';

const PRESENTATION_ICONS: React.ReactNode[] = [
  <img key="face" src="/images/car-front.png" alt="Face" className={ICON_CLASS} />,
  <img key="profil-g" src="/images/car-driver.png" alt="Profil conducteur" className="tw-w-20 tw-h-20 tw-object-contain" />,
  <img key="profil-d" src="/images/car-passenger.png" alt="Profil passager" className="tw-w-20 tw-h-20 tw-object-contain" />,
  <img key="arriere" src="/images/car-back.png" alt="Arrière" className={ICON_CLASS} />,
  <img key="soubassement" src="/images/car-undeside.png" alt="Soubassement" className={ICON_CLASS} />,
  <img key="moteur" src="/images/car-engine.png" alt="Bloc moteur" className={ICON_CLASS} />,
];

const TOOLTIPS = {
  km: "Cochez cette case pour attester du kilométrage précis du véhicule lors de la livraison. Recommandé: joignez une photo du compteur kilométrique, cette photo apparaîtra en annexe de l'attestation de livraison.",
  presentation: "Cochez cette case pour attester visuellement de l'état général du véhicule lors de la vente. Joignez une photo de face, du profil conducteur, du profil passager, de l'arrière, du soubassement, du bloc moteur.",
  observations: "Cochez cette case pour attester des observations faites à votre client le jour de la livraison. Il peut s'agir d'un élément défectueux, d'un élément manquant etc. Fortement recommandé: joignez une photo de l'élément concerné.",
};

interface ObservationItem {
  text: string;
  attachment?: DeliveryProofAttachment;
}

interface Step4VehicleInfoProps {
  onComplete?: () => void;
}

export const Step4VehicleInfo = forwardRef<WizardStepHandle, Step4VehicleInfoProps>(({ onComplete }, ref) => {
  const { submitAll, sendDeliveryProof, updateFormData, isSending } =
    useDeliveryProofForm({ onComplete });
  const deliveryProofId = useDeliveryProofStore((state) => state.deliveryProofId);
  const formData = useDeliveryProofStore((state) => state.formData);

  // État initial basé sur les données du store
  const [showVehicleBlock, setShowVehicleBlock] = useState(formData.vehicleInfo.length > 0);

  // KM
  const kmItem = formData.vehicleInfo.find((v) => v.type === DeliveryProofVehicleInfoType.KM);
  const [showKm, setShowKm] = useState(!!kmItem);
  const [kmValue, setKmValue] = useState(kmItem?.value || '');
  const [kmAttachment, setKmAttachment] = useState<DeliveryProofAttachment | undefined>(
    kmItem && toAttachment(kmItem),
  );

  // Présentation
  const presentationItems = formData.vehicleInfo
    .filter((v) => v.type === DeliveryProofVehicleInfoType.PRESENTATION)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const [showPresentation, setShowPresentation] = useState(presentationItems.length > 0);
  const [presentationFiles, setPresentationFiles] = useState<Array<string | undefined>>(
    Array(6)
      .fill(undefined)
      .map((_, i) => presentationItems[i]?.fileUrl)
  );

  // Observations
  const observationItems = formData.vehicleInfo
    .filter((v) => v.type === DeliveryProofVehicleInfoType.OBSERVATION)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const [showObservations, setShowObservations] = useState(observationItems.length > 0);
  const [observations, setObservations] = useState<ObservationItem[]>(
    observationItems.length > 0
      ? observationItems.map((o) => ({ text: o.value || '', attachment: toAttachment(o) }))
      : []
  );

  const [kmError, setKmError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Données bénéficiaire depuis le query cache
  const { data: deliveryProofData } = useDeliveryProof(deliveryProofId ?? undefined);

  const handleAddObservation = () => {
    setObservations((prev) => [...prev, { text: '' }]);
  };

  const handleRemoveObservation = (index: number) => {
    setObservations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleObservationTextChange = (index: number, text: string) => {
    const capped = capObservation(text);
    setObservations((prev) => prev.map((obs, i) => (i === index ? { ...obs, text: capped } : obs)));
  };

  const handleObservationFileChange = (index: number, attachment: DeliveryProofAttachment | undefined) => {
    setObservations((prev) => prev.map((obs, i) => (i === index ? { ...obs, attachment } : obs)));
  };

  const handlePresentationFileChange = (index: number, fileUrl: string | undefined) => {
    setPresentationFiles((prev) => prev.map((f, i) => (i === index ? fileUrl : f)));
  };

  const buildVehicleInfo = (): VehicleInfoItemDto[] => {
    const vehicleInfo: VehicleInfoItemDto[] = [];

    if (!showVehicleBlock) {
      return vehicleInfo;
    }

    // KM
    if (showKm && kmValue) {
      vehicleInfo.push({
        type: DeliveryProofVehicleInfoType.KM,
        value: kmValue,
        ...kmAttachment,
        orderIndex: 0,
      });
    }

    // Presentation
    if (showPresentation) {
      presentationFiles.forEach((fileUrl, index) => {
        if (fileUrl) {
          vehicleInfo.push({
            type: DeliveryProofVehicleInfoType.PRESENTATION,
            fileUrl,
            orderIndex: index,
          });
        }
      });
    }

    // Observations
    if (showObservations) {
      observations.forEach((obs, index) => {
        if (obs.text.trim()) {
          vehicleInfo.push({
            type: DeliveryProofVehicleInfoType.OBSERVATION,
            // Replafonné ici, à la frontière de soumission : l'état initial est
            // hydraté depuis l'API (`observationItems`) sans passer par le
            // handler de frappe, donc une valeur héritée plus longue que la
            // colonne repartirait telle quelle et reproduirait le 500.
            value: capObservation(obs.text),
            ...obs.attachment,
            orderIndex: index,
          });
        }
      });
    }

    return vehicleInfo;
  };

  // Ouvrir la modale de confirmation après sauvegarde
  const handleGenerate = async () => {
    setError(null);
    setKmError(false);

    if (!deliveryProofId) {
      setError('ID d\'attestation manquant');
      return;
    }

    if (showVehicleBlock && showKm && !kmValue) {
      setKmError(true);
      return;
    }

    try {
      const vehicleInfo = buildVehicleInfo();
      updateFormData({ vehicleInfo });
      // Chaîner tous les PATCH (warranties → elements → vehicle-info)
      await submitAll();
      setShowConfirmModal(true);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde');
    }
  };

  const handleConfirm = async (signatureType: 1 | 2, email?: string, phone?: string) => {
    try {
      setError(null);
      await sendDeliveryProof(signatureType, email, phone);
    } catch (err: any) {
      setShowConfirmModal(false);
      setError(err.message || 'Une erreur est survenue lors de la génération');
    }
  };

  // Sync live vers le store pour la preview PDF
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    const vehicleInfo = buildVehicleInfo();
    updateFormData({ vehicleInfo });
  }, [showVehicleBlock, showKm, kmValue, kmAttachment, showPresentation, presentationFiles, showObservations, observations]);

  useImperativeHandle(ref, () => ({
    handleNext: () => handleGenerate(),
    handleSave: () => {
      const vehicleInfo = buildVehicleInfo();
      updateFormData({ vehicleInfo });
    },
  }));

  const handleAutoResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-2 sm:tw-p-4">
      <div className="tw-p-4 sm:tw-p-6 tw-space-y-5">
      <form id="wizard-step-form" onSubmit={(e) => { e.preventDefault(); handleGenerate(); }} className="tw-space-y-6">
        {/* Checkbox d'activation du bloc */}
        <label id="dp-vehicle-toggle" className="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer tw-select-none">
          <input
            type="checkbox"
            checked={showVehicleBlock}
            onChange={(e) => setShowVehicleBlock(e.target.checked)}
            className="tw-mt-0.5 tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
          />
          <span className="tw-text-sm tw-text-gray-700">
            Intégrer le bloc &quot;Informations véhicule&quot; dans l'attestation de livraison (facultatif)
          </span>
        </label>

        {showVehicleBlock && (
          <div id="dp-vehicle-info-block">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-pb-3 tw-border-b tw-border-gray-100">
              <Car className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
              <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Informations véhicule</h4>
            </div>

            <div className="tw-space-y-6">
              {/* Section KM — activable par checkbox */}
              <div className="tw-space-y-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                    <input
                      type="checkbox"
                      checked={showKm}
                      onChange={(e) => setShowKm(e.target.checked)}
                      className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
                    />
                    <span className="tw-text-sm tw-text-gray-900">Kilométrage précis du véhicule le jour de la livraison</span>
                  </label>
                  <Tooltip content={TOOLTIPS.km} />
                </div>

                {showKm && (
                  <div className="tw-ml-6 tw-pl-2 tw-border-l-2 tw-border-gray-100">
                    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-items-start">
                      <div className="tw-col-span-12 md:tw-col-span-4">
                        <label className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">Kilométrage <span className="tw-text-red-500">*</span></label>
                        <div className="tw-relative">
                          <input
                            type="number"
                            value={kmValue}
                            onChange={(e) => { setKmValue(e.target.value); setKmError(false); }}
                            min={0}
                            className={`tw-block tw-w-full tw-px-3 tw-py-2 tw-pr-10 tw-text-base sm:tw-text-sm tw-border tw-rounded-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary ${kmError && !kmValue ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                          />
                          <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-xs tw-text-gray-400">km</span>
                        </div>
                      </div>
                      <div className="tw-col-span-12 md:tw-col-span-8">
                        <FileUploadField
                          deliveryProofId={deliveryProofId!}
                          value={kmAttachment}
                          onChange={setKmAttachment}
                          accept={PHOTO_ACCEPT}
                          size="sm"
                          label="Photo du compteur (facultatif)"
                        />
                      </div>
                    </div>
                    {kmError && !kmValue && (
                      <p className="tw-text-xs tw-text-red-600 tw-mt-1">Le kilométrage est obligatoire</p>
                    )}
                  </div>
                )}
              </div>

              {/* Section Présentation — activable par checkbox */}
              <div className="tw-space-y-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                    <input
                      type="checkbox"
                      checked={showPresentation}
                      onChange={(e) => setShowPresentation(e.target.checked)}
                      className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
                    />
                    <span className="tw-text-sm tw-text-gray-900">Présentation générale du véhicule</span>
                  </label>
                  <Tooltip content={TOOLTIPS.presentation} />
                </div>

                {showPresentation && (
                  <div className="tw-ml-6 tw-pl-2 tw-border-l-2 tw-border-gray-100">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-3">
                      {PRESENTATION_LABELS.map((label, index) => (
                        <FileDropzone
                          key={index}
                          deliveryProofId={deliveryProofId!}
                          value={presentationFiles[index]}
                          onChange={(fileUrl) => handlePresentationFileChange(index, fileUrl)}
                          accept={PHOTO_ACCEPT}
                          label={label}
                          compact
                          icon={PRESENTATION_ICONS[index]}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Section Observations — activable par checkbox */}
              <div className="tw-space-y-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                    <input
                      type="checkbox"
                      checked={showObservations}
                      onChange={(e) => setShowObservations(e.target.checked)}
                      className="tw-w-4 tw-h-4 tw-accent-affinicar-primary tw-cursor-pointer"
                    />
                    <span className="tw-text-sm tw-text-gray-900">Observations</span>
                  </label>
                  <Tooltip content={TOOLTIPS.observations} />
                </div>

                {showObservations && (
                  <div className="tw-ml-6 tw-pl-2 tw-border-l-2 tw-border-gray-100">
                    <div className="tw-space-y-3">
                      {observations.map((obs, index) => (
                        <div key={index} className="tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-100">
                          <div className="tw-flex tw-items-start tw-gap-2 tw-mb-2">
                            <div className="tw-flex-1">
                              <textarea
                                value={obs.text}
                                onChange={(e) => { handleObservationTextChange(index, e.target.value); handleAutoResize(e); }}
                                placeholder="Décrivez l'observation..."
                                rows={2}
                                className="tw-block tw-w-full tw-px-3 tw-py-2 tw-text-base sm:tw-text-sm tw-border tw-border-gray-200 tw-rounded-xl tw-resize-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary"
                              />
                              <div
                                className={`tw-mt-1 tw-text-xs tw-text-right ${observationLength(obs.text) >= OBSERVATION_MAX_LENGTH ? 'tw-text-amber-600' : 'tw-text-gray-400'}`}
                                // Annoncé seulement une fois la limite atteinte : en `polite`
                                // permanent, un lecteur d'écran énoncerait « 1/255 », « 2/255 »…
                                // à chaque frappe et couvrirait l'écho des caractères saisis.
                                aria-live={observationLength(obs.text) >= OBSERVATION_MAX_LENGTH ? 'polite' : 'off'}
                              >
                                {observationLength(obs.text) >= OBSERVATION_MAX_LENGTH
                                  ? `Limite atteinte — ${OBSERVATION_MAX_LENGTH}/${OBSERVATION_MAX_LENGTH} caractères. Ajoutez une observation pour la suite.`
                                  : `${observationLength(obs.text)}/${OBSERVATION_MAX_LENGTH}`}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveObservation(index)}
                              className="tw-text-red-400 hover:tw-text-red-600 tw-p-1 tw-transition-colors tw-flex-shrink-0"
                              title="Supprimer"
                            >
                              <X className="tw-w-4 tw-h-4" />
                            </button>
                          </div>
                          <FileUploadField
                            deliveryProofId={deliveryProofId!}
                            value={obs.attachment}
                            onChange={(attachment) => handleObservationFileChange(index, attachment)}
                            size="sm"
                          />
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={handleAddObservation}
                        className="tw-flex tw-items-center tw-justify-center sm:tw-justify-start tw-gap-1.5 tw-text-sm tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-dark tw-transition-colors tw-w-full sm:tw-w-auto tw-py-2 sm:tw-py-0"
                      >
                        <Plus className="tw-w-4 tw-h-4" />
                        Ajouter une observation
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Message d'erreur */}
        {error && (
          <div className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-red-800">
            {error}
          </div>
        )}
      </form>


      <DeliveryProofConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        defaultEmail={deliveryProofData?.beneficiary?.email || ''}
        defaultPhone={deliveryProofData?.beneficiary?.phone || ''}
        isSubmitting={isSending}
      />
      </div>
    </div>
  );
});

Step4VehicleInfo.displayName = 'Step4VehicleInfo';
